import { h } from 'preact';
import { PureComponent, createPortal } from 'preact/compat';
import { css } from 'react-emotion';
import Popup from '../Popup/Popup';
import { withContext } from '../../common/context';
import {
  ZoomViewerPosition,
  ZoomTrigger,
  TipPosition,
  TipShow,
  ZoomType,
  DisplayMode
} from '../../typing/enums';
import { getObjectByPrefix } from '../../utils/object';
import { getRgbaColor } from '../../utils/color';
import { Events } from '../../utils/events';
import { onTransitionEnd, setTransition, removeTransition } from '../../utils/transition';
import { isMobile } from 'mobile-device-detect';
import { mouseTracker, clickTrigger, getMouseX, getMouseY } from '../../utils/mouse';
import { FlexCenterStretched } from '../../utils/emotion';
import {
  ZoomWrapper,
  ZoomFlyoutWrapper,
  ZoomImage,
  ZoomLens,
  ZoomTriggerWrapper
} from './Zoom.styled';

class Zoom extends PureComponent {
  state = {
    left: 0,
    top: 0,
    isZoomed: false
  };

  componentDidMount() {
    if (this.wrapper) {
      this.forceUpdate();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.zoomIn !== this.props.zoomIn) {
      if (this.props.zoomIn) {
        let event = new MouseEvent('mousedown', {
          view: window,
          bubbles: true,
          cancelable: true
        });
        this.zoomIn(event, true);
      } else {
        this.zoomOut();
      }
    }
  }

  componentWillUnmount() {
    this.zoomOut();
    document.body.removeEventListener('keydown', this.handleEsc);
  }

  handleEsc = e => {
    if (e.key === 'Escape') {
      this.zoomOut();
      e.preventDefault();
    }
  };

  getCurrentAsset = () => {
    const getAssetByIndex = index =>
      index && this.props.context.config.selectMediaAssets()[this.props.index];

    const getAssetByPublicId = publicId =>
      this.props.context.config.selectMediaAssets().find(asset => asset.publicId === publicId);

    return (
      this.props.asset ||
      getAssetByIndex(this.props.index) ||
      getAssetByPublicId(this.props.publicId)
    );
  };

  getCursorPos = (e, isCustomZoom = false) => {
    let a;
    let x = 0;
    let y = 0;
    e = e || window.event;
    /*get the x and y positions of the image:*/
    a = this.wrapper.getBoundingClientRect();
    /*calculate the cursor's x and y coordinates, relative to the image:*/
    x = (isCustomZoom && this.props.zoomInX ? this.props.zoomInX : getMouseX(e)) - a.left;
    y = (isCustomZoom && this.props.zoomInY ? this.props.zoomInY : getMouseY(e)) - a.top;
    /*consider any page scrolling:*/
    x = x - window.pageXOffset;
    y = y - window.pageYOffset;

    return {
      x,
      y
    };
  };

  isInline = () => ZoomType.INLINE === this.props.type && !this.props.viewerContainer;

  isPopup = () => ZoomType.POPUP === this.props.type && !this.props.viewerContainer;

  onMove = e => {
    if (isMobile) {
      this.moveByTouch(e);
      e.stopPropagation();
    } else if (this.isInline()) {
      this.moveByMouse(e);
    } else {
      this.moveByLens(e);
    }
  };

  moveByLens = (e, isCustomZoom = false) => {
    const { level } = this.props;
    let x;
    let y;
    /*prevent any other actions that may occur when moving over the image:*/
    e.preventDefault();
    /*get the cursor's x and y positions:*/
    const pos = this.getCursorPos(e, isCustomZoom);
    const offsetShadow = this.props.context.config.selectZoomPropsLensShadow() ? 1 : 0;
    /*calculate the position of the lens:*/
    x = pos.x - this.lens.offsetWidth / level;
    y = pos.y - this.lens.offsetHeight / level;
    /*prevent the lens from being positioned outside the image:*/
    if (x > this.props.width - this.lens.offsetWidth - offsetShadow) {
      x = this.props.width - this.lens.offsetWidth - offsetShadow;
    }
    if (x < 0) {
      x = offsetShadow;
    }
    if (y > this.props.height - this.lens.offsetHeight - offsetShadow) {
      y = this.props.height - this.lens.offsetHeight - offsetShadow;
    }
    if (y < 0) {
      y = offsetShadow;
    }
    /*set the position of the lens:*/
    this.lens.style.left = `${x}px`;
    this.lens.style.top = `${y}px`;
    /*display what the lens "sees":*/
    this.setState(() => ({
      left: x * level * -1,
      top: y * level * -1
    }));
  };

  moveByTouch = e => {
    e.preventDefault();
    if (this.mouseTracker) {
      const level = this.props.level - 1;
      const touchPos = this.getCursorPos(e);
      let x = touchPos.x * level;
      let y = touchPos.y * level;
      if (x > this.props.width * level) {
        x = x > this.props.width ? this.props.width * level : x;
      }
      if (x < 0) {
        x = 0;
      }
      if (y > this.props.height * level) {
        y = this.props.height * level;
      }
      if (y < 0) {
        y = 0;
      }
      this.setState(() => ({
        left: x * -1,
        top: y * -1
      }));
    }
  };

  moveByMouse = e => {
    e.preventDefault();
    const mousePos = this.getCursorPos(e);
    this.setState(() => ({
      left: mousePos.x * -(this.props.level - 1),
      top: mousePos.y * -(this.props.level - 1)
    }));
  };

  onMouseDown = event => {
    this.mouseClickTracker = clickTrigger(event);
    this.mouseTracker = mouseTracker(event);
  };

  onMouseUp = event => {
    if (this.mouseClickTracker && this.mouseClickTracker(event)) {
      if (this.state.isZoomed) {
        this.zoomOut();
      } else {
        this.zoomIn(event);
      }
      if (isMobile) {
        event.stopPropagation();
      }
    }
  };

  mouseOver = event => {
    if (this.props.trigger === ZoomTrigger.HOVER) {
      this.zoomIn(event);
    }
  };

  mouseOut = e => {
    if (
      e.offsetX >= this.props.width ||
      e.offsetX < 0 ||
      e.offsetY >= this.props.height ||
      e.offsetY < 0
    ) {
      this.zoomOut();
    }
  };

  touchStart = event => {
    this.onMouseDown(event);
    if (this.state.isZoomed) {
      event.stopPropagation();
    }
  };

  touchEnd = event => {
    this.onMouseUp(event);
    if (this.state.isZoomed) {
      event.stopPropagation();
    }
  };

  getWrapperCoords = () => {
    if (this.wrapper && !this.props.viewerContainer) {
      const { height, width, context } = this.props;
      const position = this.props.viewerPosition;
      const viewerOffset = context.config.selectZoomPropsViewerOffset();
      const rect = this.wrapper.getBoundingClientRect();
      const scrollLeft = window.pageXOffset;
      const scrollTop = window.pageYOffset;
      const displayMode = context.config.selectDisplayPropsMode();
      const displaySpacing = context.config.selectDisplayPropsSpacing();
      const displayColumns = context.config.selectDisplayPropsColumns();
      const isMultiColumns = displayMode === DisplayMode.EXPANDED && displayColumns > 1;
      const offset = isMultiColumns ? displaySpacing : viewerOffset;
      let top;
      if (this.isInline()) {
        top = 0;
      } else if (position === ZoomViewerPosition.BOTTOM) {
        top = scrollTop + rect.top + height + offset;
      } else if (position === ZoomViewerPosition.TOP) {
        top = scrollTop + rect.top - height - offset;
      } else {
        top = rect.top + scrollTop;
      }
      let left;
      if (this.isInline()) {
        left = 0;
      } else if (position === ZoomViewerPosition.RIGHT) {
        left = scrollLeft + rect.right + offset;
      } else if (position === ZoomViewerPosition.LEFT) {
        left = scrollLeft + rect.left - width - offset;
      } else {
        left = rect.left + scrollLeft;
      }

      return { y: top, x: left };
    }

    return { y: 0, x: 0 };
  };

  zoomIn = (event, isCustomZoom = false) => {
    this.setState(() => ({
      isZoomed: true
    }));
    if (!this.isPopup()) {
      this.setTransition();
      if (isMobile) {
        this.moveByTouch(event);
      } else {
        this.moveByLens(event, isCustomZoom);
      }
    }
    this.props.context.events(Events.ZOOM_IN, this.props.publicId);
    this.props.onZoomIn && this.props.onZoomIn();
    document.body.addEventListener('keydown', this.handleEsc);
  };

  zoomOut = () => {
    if (!this.isPopup()) {
      this.setTransition();
    }
    this.setState(() => ({
      isZoomed: false
    }));
    if (this.props.onZoomOut) {
      this.props.onZoomOut();
    }
    if (this.props.setZoom) {
      this.props.setZoom(-1);
    }
    this.props.context.events(Events.ZOOM_OUT, this.props.publicId);
    document.body.removeEventListener('keydown', this.handleEsc);
  };

  setTransition = () => {
    const duration = 150;
    if (this.zoomWrapper && this.isInline() && !isMobile) {
      setTransition(this.zoomWrapper, `all ${duration}ms`);
      const transitionEndCb = () => {
        if (!this.state.isZoomed && this.props.onZoomOut) {
          this.props.onZoomOut();
        }
        removeTransition(this.zoomWrapper);
      };
      onTransitionEnd(this.zoomWrapper, transitionEndCb, duration);
    }
  };

  shouldRenderTip = () => {
    const config = this.props.context.config;
    const showTipConfig = config.selectZoomPropsShowTip();

    // if isZoomed or showTip is Never return false, if isMobile and desktop or not mobile and touch then return false;
    return !(
      this.state.isZoomed ||
      showTipConfig === TipShow.NEVER ||
      (isMobile && showTipConfig === TipShow.DESKTOP) ||
      (!isMobile && showTipConfig === TipShow.TOUCH)
    );
  };

  renderInfo = () => {
    const config = this.props.context.config;
    const position = config.selectZoomPropsTipPosition();
    const msgCls = css({
      backgroundColor: getRgbaColor(config.selectTipPropsColor(), config.selectTipPropsOpacity()),
      color: config.selectTipPropsTextColor(),
      padding: '8px 12px',
      borderRadius: config.selectTipPropsRadius(),
      marginBottom: position === TipPosition.CENTER || position === TipPosition.BOTTOM ? '12px' : 0,
      marginTop: position === TipPosition.TOP ? '12px' : 0,
      fontSize: '1.3em'
    });

    return this.shouldRenderTip() ? (
      <FlexCenterStretched
        data-test="zoom-media-icon-wrap"
        absolute
        className={css({
          flexDirection: 'column',
          opacity: this.state.isZoomed ? 0 : 1,
          transition: 'opacity .25s ease-in',
          borderRadius: config.selectTipPropsRadius(),
          justifyContent:
            config.selectZoomPropsTipPosition() === TipPosition.BOTTOM
              ? 'flex-end'
              : config.selectZoomPropsTipPosition() === TipPosition.TOP
              ? 'flex-start'
              : 'center'
        })}
      >
        <div className={msgCls} data-test="tip-wrap">
          {isMobile ? config.selectZoomPropsTipTouchText() : config.selectZoomPropsTipText()}
        </div>
      </FlexCenterStretched>
    ) : null;
  };

  setContainerStyles = () => {
    if (this.props.viewerContainer) {
      const elem = document.querySelector(this.props.viewerContainer);
      if (elem) {
        if (!this.state.isZoomed) {
          elem.classList.remove(this.containerCls);
        } else {
          const cls = css`
            overflow: hidden;
            width: ${this.props.width}px;
            height: ${this.props.height}px;
            ${!elem.style.position ? 'position: relative;' : ''};
          `;
          elem.classList.add((this.containerCls = cls));
        }
      }
    }
  };

  render(props, state) {
    const { config } = props.context;
    const lensWidth = props.width / props.level;
    const lensHeight = props.height / props.level;
    const position = config.selectZoomPropsViewerPosition();
    const lensProps = getObjectByPrefix('lens', config.selectZoomProps());
    const zoomContainer = props.viewerContainer;
    const zoomImageStyle = {
      left: `${!state.isZoomed ? 0 : state.left}px`,
      top: `${!state.isZoomed ? 0 : state.top}px`
    };
    if (zoomContainer) {
      this.setContainerStyles();
    }

    return (
      <ZoomWrapper innerRef={elem => (this.wrapper = elem)} data-test="zoom-wrap">
        {this.wrapper && this.isInline() && !this.isPopup() ? (
          <ZoomFlyoutWrapper
            width={props.width}
            height={props.height}
            radius={config.selectZoomPropsViewerRadius()}
            url={props.url}
            left={state.left}
            top={state.top}
            zoomed={state.isZoomed}
            coords={state.isZoomed ? this.getWrapperCoords() : { x: 0, y: 0 }}
            position={position}
            spacing={config.selectZoomPropsViewerOffset()}
            level={props.level}
            useOpacity={false}
            data-test="zoom-flyout-wrap"
          >
            <ZoomImage
              itemprop="image"
              src={props.url}
              width={props.width}
              height={props.height}
              style={zoomImageStyle}
              url={props.url}
              alt={props.alt}
              zoomed={state.isZoomed}
              level={props.level}
              innerRef={elem => (this.zoomWrapper = elem)}
            />
          </ZoomFlyoutWrapper>
        ) : null}
        {this.wrapper && !this.isPopup() && (!this.isInline() || zoomContainer)
          ? createPortal(
              <ZoomFlyoutWrapper
                zIndex={config.selectZoomPropsViewerZIndex()}
                width={props.width}
                height={props.height}
                radius={config.selectZoomPropsViewerRadius()}
                url={props.url}
                left={state.left}
                top={state.top}
                coords={state.isZoomed ? this.getWrapperCoords() : { x: -1000000, y: -1000000 }}
                position={position}
                spacing={config.selectZoomPropsViewerOffset()}
                level={props.level}
                renderToContainer={zoomContainer}
                zoomed={state.isZoomed}
                useOpacity={true}
                data-test="zoom-flyout-wrap"
              >
                <ZoomImage
                  itemprop="image"
                  src={props.url}
                  width={props.width}
                  height={props.height}
                  style={zoomImageStyle}
                  url={props.url}
                  zoomed={state.isZoomed}
                  level={props.level}
                />
              </ZoomFlyoutWrapper>,
              (zoomContainer && document.querySelector(zoomContainer)) || document.body
            )
          : null}
        {!this.isPopup() ? (
          <ZoomLens
            show={state.isZoomed && !this.isInline() && config.selectZoomPropsShowLens()}
            width={lensWidth}
            height={lensHeight}
            radius={config.selectZoomPropsLensRadius()}
            innerRef={elem => (this.lens = elem)}
            {...lensProps}
            data-test="zoom-lens"
          />
        ) : null}
        {this.renderInfo()}
        {this.isPopup() && this.state.isZoomed ? (
          <Popup
            alt={this.props.alt}
            onClose={this.zoomOut}
            asset={this.getCurrentAsset()}
            {...config.selectZoomPopupProps()}
          />
        ) : null}
        <ZoomTriggerWrapper
          data-test="zoom-trigger"
          innerRef={elem => (this.trigger = elem)}
          zoomed={state.isZoomed}
          onMouseDown={
            !isMobile && props.trigger === ZoomTrigger.CLICK ? this.onMouseDown : undefined
          }
          onMouseUp={!isMobile && props.trigger === ZoomTrigger.CLICK ? this.onMouseUp : undefined}
          onMouseMove={
            (!isMobile && !this.isPopup() && state.isZoomed) || state.isZoomed
              ? this.onMove
              : undefined
          }
          onMouseOut={
            !isMobile && this.state.isZoomed && !this.isPopup() ? this.mouseOut : undefined
          }
          onMouseOver={!isMobile && !this.isPopup() ? this.mouseOver : undefined}
          onTouchStart={isMobile ? this.touchStart : undefined}
          onTouchMove={isMobile && state.isZoomed && !this.isPopup() ? this.onMove : undefined}
          onTouchEnd={isMobile ? this.touchEnd : undefined}
          onTouchCancel={isMobile ? this.touchEnd : undefined}
        />
      </ZoomWrapper>
    );
  }
}

export default withContext(Zoom);
